import type { ReplayEvent } from "../types.js";

export const SPEED_PRESETS: { label: string; msPerEvent: number | "instant" }[] =
  [
    { label: "0.25×", msPerEvent: 1600 },
    { label: "0.5×", msPerEvent: 800 },
    { label: "1×", msPerEvent: 400 },
    { label: "2×", msPerEvent: 200 },
    { label: "5×", msPerEvent: 80 },
    { label: "10×", msPerEvent: 40 },
    { label: "Instant", msPerEvent: "instant" },
  ];

export interface PlaybackControlsProps {
  index: number;
  count: number;
  playing: boolean;
  speedLabel: string;
  showHidden: boolean;
  showCoords: boolean;
  onIndexChange: (idx: number) => void;
  onPrevEvent: () => void;
  onNextEvent: () => void;
  onPrevTurn: () => void;
  onNextTurn: () => void;
  onPlayPause: () => void;
  onSpeed: (label: string) => void;
  onToggleHidden: () => void;
  onToggleCoords: () => void;
  onJump: (kind: JumpKind) => void;
  jumpAvailability: Record<JumpKind, boolean>;
  currentEvent: ReplayEvent | undefined;
}

export type JumpKind =
  | "first_combat"
  | "first_compass"
  | "first_landmark"
  | "treasure_dug"
  | "next_handoff"
  | "final_escape";

const JUMP_LABELS: Record<JumpKind, string> = {
  first_combat: "First combat",
  first_compass: "First Compass",
  first_landmark: "First Landmark",
  treasure_dug: "Treasure dug",
  next_handoff: "Next handoff",
  final_escape: "Final escape",
};

export function PlaybackControls(props: PlaybackControlsProps) {
  const {
    index,
    count,
    playing,
    speedLabel,
    showHidden,
    showCoords,
    onIndexChange,
    onPrevEvent,
    onNextEvent,
    onPrevTurn,
    onNextTurn,
    onPlayPause,
    onSpeed,
    onToggleHidden,
    onToggleCoords,
    onJump,
    jumpAvailability,
    currentEvent,
  } = props;

  return (
    <div className="controls">
      <div className="controls__row">
        <button onClick={onPrevTurn} title="Previous turn">« turn</button>
        <button onClick={onPrevEvent} title="Previous event">‹ event</button>
        <button onClick={onPlayPause} className="controls__play">
          {playing ? "❚❚ Pause" : "▶ Play"}
        </button>
        <button onClick={onNextEvent} title="Next event">event ›</button>
        <button onClick={onNextTurn} title="Next turn">turn »</button>
        <span className="controls__pos">
          Event {index}/{count}
          {currentEvent && ` · T${currentEvent.turn} · ${currentEvent.type}`}
        </span>
      </div>
      <div className="controls__row">
        <input
          type="range"
          min={0}
          max={count}
          step={1}
          value={index}
          onChange={(e) => onIndexChange(Number(e.target.value))}
          className="controls__scrubber"
        />
      </div>
      <div className="controls__row">
        <span className="controls__label">Speed</span>
        {SPEED_PRESETS.map((s) => (
          <button
            key={s.label}
            onClick={() => onSpeed(s.label)}
            className={
              s.label === speedLabel
                ? "controls__speed controls__speed--on"
                : "controls__speed"
            }
          >
            {s.label}
          </button>
        ))}
      </div>
      <div className="controls__row">
        <span className="controls__label">Jump</span>
        {(Object.keys(JUMP_LABELS) as JumpKind[]).map((k) => (
          <button
            key={k}
            onClick={() => onJump(k)}
            disabled={!jumpAvailability[k]}
            className="controls__jump"
          >
            {JUMP_LABELS[k]}
          </button>
        ))}
      </div>
      <div className="controls__row">
        <label>
          <input
            type="checkbox"
            checked={showHidden}
            onChange={onToggleHidden}
          />{" "}
          Show hidden info
        </label>
        <label>
          <input
            type="checkbox"
            checked={showCoords}
            onChange={onToggleCoords}
          />{" "}
          Show coordinates
        </label>
      </div>
    </div>
  );
}
